/**
 * Several named plans for one owner — a list, and the detail of the one opened.
 *
 * **A table, not cards.** The three things that tell one plan from another are its
 * name, what it covers, and the sweeps it runs, and those are columns. Cards made
 * seven near-identical "Class plan" rows genuinely unreadable — the screenshot
 * that prompted this had two of them side by side, distinguishable only by
 * counting.
 *
 * **The detail is two sections, in this order.** Metadata first (name, passes,
 * subjects) — what the plan IS — then the chapter schedule — how it gets there.
 * They answer different questions and the first cut interleaved them, so a
 * thirty-row grid pushed everything that defines the plan out of sight.
 *
 * This component only exists because a plan stopped being a slot. When a class
 * could hold exactly one plan per subject there was nothing to choose between and
 * the editor could fetch "the" plan; now a class can offer *"Steady — 3
 * revisions"* and *"Catch-up from October"* side by side, and picking is the
 * student's whole decision.
 *
 * **Why several plans rather than one adjustable plan.** A student with four
 * months left does not need the six-month plan compressed — they need one with
 * fewer passes, scoped differently. That is an editorial judgement a human makes
 * by authoring a second plan, which is why this surface is a list.
 *
 * Deliberately NOT here: adopting. Taking a plan writes the student's own
 * schedule and belongs with their plan, not with the authoring surface — the
 * admin console renders this panel too, and an admin has no plan to adopt into.
 *
 * See docs/FeatureDesigns/goals-syllabus-revision-plan/StudyPlanning/shared-plans.md.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PlanEditor, VISIBILITY_LABEL, type PlanDraft } from './PlanEditor';
import { PlanChapterSchedule } from './PlanChapterSchedule';
import { ResponsiveTable, type ResponsiveColumn } from '../responsive';
import type { RowMenuItem } from '../components/RowContextMenu';
import { formatPlanDate, NO_DATE, planElapsed } from './planDates';
import { DEFAULT_STUDY_PASSES } from '@padcanvas/shared';
import { useConfirm } from '../components/useConfirm';
import { Modal } from '../components/Modal';
import type {
  StudyPlanApi, StudyPlan, StudyPlanOwnerKind, StudyPlanVisibility, AdoptOutcome,
} from './types';
import './class-shared.css';

/** Server-enforced too, and rejected rather than truncated there — a name
 *  silently cut at 80 is the author's words misquoted in front of strangers. */
/** What a plan is called between "New plan" and the name its author types.
 *  Visible only in the row behind the screen they land on, and never left
 *  behind: backing out deletes the row. */
const UNTITLED = 'Untitled plan';


/**
 * Stable identity for the `additionalPlans` default.
 *
 * An inline `= []` in the destructuring is a NEW array every render, which made
 * `refresh` a new callback every render, which made the effect that calls it fire
 * every render — each one setting state and re-rendering. Callers that pass the
 * prop (the student app, from useState) were fine; the admin Plan and Study Plans
 * tabs omit it, so they spun on "Loading…" re-requesting listPlans forever.
 */
const NO_ADDITIONAL_PLANS: readonly StudyPlan[] = Object.freeze([]);

/** Whose plan this is, in the words a student would use. The table mixes owners,
 *  so a row that does not say where it came from is unreadable.
 *
 *  The platform is NAMED rather than called "us": the reader of this table is
 *  often a school's own administrator, for whom "us" is the school. */
function sourceLabel(p: StudyPlan): string {
  if (p.ownerKind === 'platform') return 'From BuzzBrain';
  if (p.ownerKind === 'schoolClass') return 'Your class';
  return 'Yours';
}

export function PlansPanel({
  api, ownerKind, ownerId, subjectName, availableSubjects = [],
  chaptersFor, canEdit = true, canEditPlan, canPublish = true,
  canPublishPlan = (p) => p.ownerKind === 'user', boardGradeOptions, showPlan,
  onSelectionChange, detailIn = 'page',
  onAdopt, additionalPlans = NO_ADDITIONAL_PLANS, activePlanId, scopeToSubject, listHeader,
  omitColumns,
  boardGrade, canSetDefault = false, headerActions,
}: {
  api: StudyPlanApi;
  ownerKind: StudyPlanOwnerKind;
  /** Omitted for a personal plan — the server reads "me" from the token. */
  ownerId?: string;
  /** A subject's internal name is a key, never a label. */
  subjectName?: (subject: string) => string;
  /** Subjects this owner could plan, each with the syllabus it would be pinned
   *  to. A plan covers a SET of them, so this is a menu rather than a filter. */
  availableSubjects?: Array<{ subject: string; curriculumId?: string }>;
  /** The chapters of one subject's syllabus, for the chapter-dates screen. A
   *  function rather than an array because the panel spans subjects and cannot
   *  know up front which one the reader will open. */
  chaptersFor?: (subject: string) => Array<{ id: string; number?: string; name: string }>;
  /**
   * Where a plan's detail opens.
   *
   * `page` replaces the list, which suits a console tab that has the whole page.
   * `dialog` keeps the list in place and floats the detail, which is what a
   * surface embedded in a longer screen needs — inline detail there pushed
   * everything below it out of reach.
   */
  detailIn?: 'page' | 'dialog';
  /**
   * Whether THIS plan may be edited, not whether the panel may edit anything.
   *
   * A student's surface lists their own plans beside plans they merely took —
   * their class's, the platform's, a stranger's — and a panel-wide flag offered
   * Edit and Delete on all of them. Owning a copy is not owning the original.
   */
  canEditPlan?: (plan: StudyPlan) => boolean;
  /** Given = an Adopt button per row. Absent = an authoring-only surface. */
  /** Returns what the take actually did, when the caller knows — a plan spans
   *  subjects and writes one document each, so "it worked" is often a half-truth. */
  onAdopt?: (plan: StudyPlan) => void | Promise<void | AdoptOutcome>;
  /**
   * Plans from OTHER owners, shown in the same table.
   *
   * A student's question is "which plan shall I follow", and the answer spans
   * their own drafts, their class's, and the platform's. Splitting them behind an
   * owner picker made them look like three unrelated lists and left the common
   * case — one shelf, empty — showing nothing at all.
   */
  additionalPlans?: readonly StudyPlan[];
  /** The plan this reader is currently following, marked in the table. Without it
   *  a list of plans says nothing about which one is in force. */
  activePlanId?: string;
  canEdit?: boolean;
  /** Whether this surface may make a plan visible to others at all. Off for a
   *  student whose publish gate is closed — the plans still work, privately. */
  canPublish?: boolean;
  /**
   * Whether THIS plan's visibility may be changed, mirroring the server's
   * `mayPublish`: an author may publish their own personal plan, and staff may
   * publish anything.
   *
   * Defaults to personal-plans-only, which is the student rule — *"any one of
   * thirty members may hand the room's plan to strangers"* is refused there.
   * A staff surface passes `() => true`, and the server still decides.
   */
  canPublishPlan?: (plan: StudyPlan) => boolean;
  /** The (board, grade) pairs a plan may be written for. Given = the editor's
   *  Written-for block becomes editable; see PlanEditor. */
  boardGradeOptions?: Array<{ board: string; grades: string[] }>;
  /**
   * The plan currently open, reported up.
   *
   * A plan now says which board and grade it is FOR, and that — not the page's
   * filter bar — is what its subject chips and chapter dates must resolve
   * against. Without this the page could only offer what its own filters were
   * set to, so opening a CBSE Grade 12 plan while the filters said "all levels"
   * offered nothing, and every subject reported "there is no curriculum".
   */
  onSelectionChange?: (plan: StudyPlan | null) => void;
  /**
   * Which rows the caller's filters leave on screen — applied to EVERY plan, the
   * ones this panel fetched as well as `additionalPlans`.
   *
   * A predicate rather than a pre-filtered list, because the panel owns what it
   * fetched: it creates into that list and deletes from it, so a caller handing
   * back a narrowed copy would be a second opinion about what the list contains.
   */
  showPlan?: (plan: StudyPlan) => boolean;
  /** Show only the reader's own plans covering this subject. For a surface that
   *  is ABOUT one subject; omit on a surface that lists plans in general. */
  scopeToSubject?: string;
  /** Column keys to leave out.
   *
   *  The console is an oversight surface and wants every column; a student is
   *  choosing a plan to follow and cannot act on half of them. "Who else can see
   *  this" and "which board and grade" are the owner's questions, and both cost
   *  width the plan's own name and its passes need. A list rather than a flag per
   *  column, so the next one costs nothing. */
  omitColumns?: readonly string[];
  /**
   * Controls that belong to the LIST — a filter bar, typically.
   *
   * Rendered by the panel rather than above it, because only the panel knows
   * whether the list is on screen. A caller placing its own filter bar left it
   * hanging over the plan detail, filtering a list nobody could see.
   */
  listHeader?: ReactNode;
  /**
   * The board and grade a plan authored here is FOR — stamped on it at creation.
   *
   * With them, adding a subject needs no syllabus lookup: the server resolves the
   * curriculum from (board, grade, subject). Without them the panel keeps working
   * exactly as before, pinning each subject to the id `availableSubjects` names.
   */
  boardGrade?: { board?: string; grade?: string };
  /** Whether this surface may name a level's default plan — the admin console
   *  alone. Passed to the editor, which draws the control. */
  canSetDefault?: boolean;
  /** Extra control(s) on the list's header row, rendered before New plan. For
   *  another way to MAKE a plan — anything acting on a row belongs in the row. */
  headerActions?: ReactNode;
}) {
  /** The OWNER's plans. Plans from elsewhere (`additionalPlans`) are merged in
   *  at render rather than copied in here — they arrive on their own schedule,
   *  and folding them into this state made their arrival re-run the fetch below. */
  const [ownPlans, setOwnPlans] = useState<StudyPlan[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Said out loud after an act that changes nothing visible on this screen. */
  const [note, setNote] = useState<string | null>(null);
  /**
   * The plan a just-clicked "New plan" created, while it is still untouched.
   *
   * A plan must exist before its detail screen can open — the screen edits a
   * saved plan, its subjects and its chapter dates, each through its own route.
   * So creating happens first and this remembers which row was born that way, so
   * backing out of the screen takes the empty row with it rather than leaving an
   * "Untitled plan" nobody asked for.
   */
  const [freshId, setFreshId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listPlans({ ownerKind, ownerId });
      /**
       * `scopeToSubject` keeps a per-subject surface to the plan it is about. A
       * student's own plan is one document per (subject, syllabus) and is created
       * for them the moment they open a subject, so a student taking nine subjects
       * owns nine — and unscoped, the progress page for Hindi listed all nine, each
       * called "My plan". The other eight say nothing about the page they are on.
       */
      setOwnPlans((res.plans ?? []).filter(p => !scopeToSubject
        || (p.subjects ?? []).some(x => x.subject === scopeToSubject)));
      setError(null);
    } catch (e) {
      /**
       * Say so, and keep what is on screen.
       *
       * A failed fetch used to be swallowed and the list emptied, so on a surface
       * that also shows plans from elsewhere the reader saw those alone — their
       * own plan looked DELETED rather than un-fetched, which is the one reading
       * that sends someone hunting for a bug in the wrong place.
       */
      setError((e as Error).message);
    } finally { setLoading(false); }
  }, [api, ownerKind, ownerId, scopeToSubject]);

  useEffect(() => { void refresh(); }, [refresh]);

  /**
   * What the list shows: the owner's plans, then everyone else's, deduped by id
   * because a class plan can legitimately arrive from both sources.
   *
   * Derived, not stored. `additionalPlans` is fetched by the CALLER and lands
   * one render late; when this merge lived inside `refresh`, that late arrival
   * changed the callback's identity, re-ran the effect and re-fetched the
   * owner's plans — so the list painted as complete with one row and then
   * visibly grew to two.
   */
  const plans = useMemo(() => {
    const seen = new Set(ownPlans.map(p => p.id));
    const all = [...ownPlans, ...additionalPlans.filter(p => !seen.has(p.id))];
    return showPlan ? all.filter(showPlan) : all;
    // Keyed on the ids rather than the array, so a caller passing an inline
    // literal does not rebuild this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownPlans, additionalPlans.map(p => p.id).join(','), showPlan]);

  /**
   * Keep the current selection across a refresh, and open NOTHING otherwise.
   *
   * This used to fall back to `plans[0]`, from when the panel was a picker that
   * always had one plan on screen. Now that opening a plan means a dialog over
   * the list, that fallback meant merely opening the list threw the first plan
   * in the reader's face — with no way to tell it apart from a plan they chose.
   */
  useEffect(() => {
    setSelectedId(prev => (prev && plans.some(p => p.id === prev) ? prev : null));
  }, [plans]);

  const selected = plans.find(p => p.id === selectedId) ?? null;

  useEffect(() => { onSelectionChange?.(selected); }, [selected, onSelectionChange]);


  /** A saved plan replaces its own row in place rather than triggering a reload —
   *  the editor saves on every cell, and refetching the list each time would
   *  flicker the picker under the cursor. */
  const replace = (plan: StudyPlan) =>
    setOwnPlans(prev => prev.map(p => (p.id === plan.id ? plan : p)));

  /**
   * Create and open, with nothing in between.
   *
   * Naming used to happen in a row above the table — type a name, press Create,
   * and only then see the screen where every other field lives. That put one
   * field in a different place from its nine siblings and made "Create" mean
   * "begin", which is not what the word says. The name is now just the first
   * field of the plan screen.
   */
  const create = async () => {
    const name = UNTITLED;
    setBusy(true); setError(null);
    try {
      // Created empty: a plan is named first and its subjects chosen after, so
      // naming is not blocked behind a subject picker.
      const res = await api.createPlan({
        ownerKind, ownerId, name,
        board: boardGrade?.board, grade: boardGrade?.grade,
      });
      setOwnPlans(prev => [res.plan, ...prev]);
      setSelectedId(res.plan.id);
      /**
       * Opened IN EDIT MODE. A plan just created is empty by construction — no
       * subjects, no dates — and opening it read-only showed a form of dashes
       * with no way in, because the dialog deliberately carries no action row and
       * Edit lives on the row behind it. The person who typed the name is
       * unambiguously there to fill it in.
       */
      // Empty name in the draft, not the placeholder: the field is the first
      // thing to fill in, and pre-filling it with a word nobody chose makes them
      // clear it first. Save stays disabled until it has one.
      setDraft({
        name: '', description: '', subjects: [],
        board: res.plan.board, grade: res.plan.grade,
      });
      setEditing(true);
      setFreshId(res.plan.id);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  /**
   * Deleting a plan is permanent and was one unguarded click.
   *
   * The tooltip said adopters keep their copy — true, and beside the point: the
   * authored plan itself, its passes and every chapter date on it, are gone with
   * no undo. So the dialog names the plan being deleted rather than asking "are
   * you sure", which is a question nobody reads.
   */
  const remove = async (plan: StudyPlan) => {
    const dated = (plan.subjects ?? []).reduce(
      (n, sub) => n + Object.values(sub.chapterTargets ?? {}).reduce((m, t) => m + t.length, 0), 0);
    const ok = await confirm({
      message: (
        <>
          <p>Delete <strong>{plan.name}</strong>?</p>
          <p>
            {/* What is actually lost, counted — "this cannot be undone" tells the
                reader nothing about what they are about to lose. */}
            {(plan.passes ?? []).length} {(plan.passes ?? []).length === 1 ? 'pass' : 'passes'}
            {(plan.subjects ?? []).length > 0 && ` across ${(plan.subjects ?? []).length} `
              + `${(plan.subjects ?? []).length === 1 ? 'subject' : 'subjects'}`}
            {dated > 0 && `, and ${dated} chapter ${dated === 1 ? 'date' : 'dates'}`}
            {' '}will be deleted. This cannot be undone.
          </p>
          {(plan.adoptions ?? 0) > 0 && (
            <p>
              {plan.adoptions} {plan.adoptions === 1 ? 'student who' : 'students who'} already
              took it keep their own copy — deleting this does not change their schedule.
            </p>
          )}
        </>
      ),
      confirmLabel: 'Delete plan',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setBusy(true); setError(null);
    try {
      await api.deletePlan(plan.id, plan.ownerId);
      setOwnPlans(prev => prev.filter(p => p.id !== plan.id));
      setSelectedId(prev => (prev === plan.id ? null : prev));
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  /**
   * Save the fields that actually changed, then the subjects.
   *
   * Details and subjects are separate routes — one is what the plan SAYS, the
   * other what it COVERS — so an unchanged half is never rewritten. Passes and
   * chapter dates are NOT here: they write one cell per change as it is made, so
   * each history entry stays exactly what moved.
   */
  /**
   * Leave the edit screen — and take an untouched new plan with you.
   *
   * "New plan" has to create before it can open, so cancelling out of that
   * screen would otherwise leave an "Untitled plan" in the list: a row nobody
   * asked for, from an action they backed out of.
   */
  const cancelEdit = async () => {
    const abandoned = freshId && selected?.id === freshId ? selected : null;
    setEditing(false); setDraft(null); setError(null); setFreshId(null);
    if (!abandoned) return;
    setSelectedId(null);
    try {
      await api.deletePlan(abandoned.id, abandoned.ownerId);
      setOwnPlans(prev => prev.filter(p => p.id !== abandoned.id));
    } catch {
      // Leaving the empty row is better than blocking the exit on a failed
      // delete; a refresh will show it and it can be deleted by hand.
      void refresh();
    }
  };

  const saveDraft = async (plan: StudyPlan) => {
    if (!draft) return;

    /**
     * Re-aiming a plan that already holds subjects throws its chapter dates away
     * — they are placement ids of the syllabus it is leaving. The editor says so
     * as you pick, and this is the last word before the dates are actually gone.
     */
    const relevelling = (draft.board !== plan.board || draft.grade !== plan.grade)
      && draft.subjects.length > 0;
    if (relevelling && !(await confirm({
      message: `Change what this plan is written for? Each subject re-pins to the `
        + `${[draft.board, draft.grade && `Grade ${draft.grade}`].filter(Boolean).join(' ')} syllabus `
        + 'and its chapter dates are cleared — the chapter plan has to be made again. '
        + 'The pass dates are kept.',
      confirmLabel: 'Change and clear dates',
      confirmVariant: 'danger',
    }))) return;

    setBusy(true); setError(null);
    try {
      let latest = plan;
      const patch: Record<string, unknown> = {};
      if (draft.name.trim() && draft.name.trim() !== plan.name) patch.name = draft.name.trim();
      // Only when it actually moved: sending it unchanged would make every save
      // of a default plan re-clear and re-set the level's default.
      if (draft.isDefault !== undefined && draft.isDefault !== !!plan.isDefault) {
        patch.isDefault = draft.isDefault;
      }
      if ((draft.description.trim() || undefined) !== plan.description) {
        patch.description = draft.description.trim() || undefined;
      }
      if (Object.keys(patch).length > 0) {
        latest = (await api.editPlanDetails(plan.id, { ownerId: plan.ownerId, ...patch })).plan;
      }

      const current = (plan.subjects ?? []).map(x => x.subject);
      // One request per ACTUAL change, in sequence: the route adds or removes a
      // single subject, resending unchanged ones would rewrite their chapter dates
      // for nothing, and running them together would race on one document.
      for (const subject of draft.subjects.filter(x => !current.includes(x))) {
        latest = (await api.editPlanSubjects(plan.id, {
          ownerId: plan.ownerId, subject,
          curriculumId: availableSubjects.find(a => a.subject === subject)?.curriculumId,
          include: true,
        })).plan;
      }
      for (const subject of current.filter(x => !draft.subjects.includes(x))) {
        latest = (await api.editPlanSubjects(plan.id, {
          ownerId: plan.ownerId, subject, include: false,
        })).plan;
      }

      /**
       * What the plan is FOR, last of all — after the subject edits, so that
       * removing a subject the new level does not teach and re-aiming the plan
       * are ONE save rather than two rounds through the editor.
       */
      if (draft.board !== plan.board || draft.grade !== plan.grade) {
        latest = (await api.editPlanDetails(plan.id, {
          ownerId: plan.ownerId, board: draft.board, grade: draft.grade,
        })).plan;
      }

      replace(latest);
      setEditing(false);
      setDraft(null);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  const setVisibility = async (plan: StudyPlan, visibility: StudyPlanVisibility) => {
    setBusy(true); setError(null);
    try {
      await api.setPlanVisibility(plan.id, { ownerId: plan.ownerId, visibility });
      replace({ ...plan, visibility });
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  /**
   * Make this plan the one students at its level are started on — or stop it
   * being that.
   *
   * The same write the editor's checkbox makes; offered here because choosing
   * the default is a decision ABOUT the set, taken while looking at all the
   * levels side by side, and reaching it meant opening a plan, entering edit
   * mode, ticking a box and saving.
   *
   * The level's previous holder is un-defaulted by the SERVER. It is cleared
   * locally too — not to do the work, but because the list would otherwise show
   * two Default badges until something refetched, which reads as a bug in the
   * rule rather than a stale row.
   */
  const setAsDefault = async (plan: StudyPlan, isDefault: boolean) => {
    const level = [plan.board, plan.grade && `Grade ${plan.grade}`].filter(Boolean).join(' · ');
    const previous = isDefault
      ? plans.find(p => p.isDefault && p.id !== plan.id
        && p.board === plan.board && p.grade === plan.grade)
      : undefined;
    if (previous && !(await confirm({
      message: `“${previous.name}” is the default for ${level} today. Make “${plan.name}” `
        + 'the default instead? Students already started on the old one keep it — this only '
        + 'changes what new students get.',
      confirmLabel: 'Make it the default',
    }))) return;

    setBusy(true); setError(null); setNote(null);
    try {
      const updated = (await api.editPlanDetails(plan.id, {
        ownerId: plan.ownerId, isDefault,
      })).plan;
      if (isDefault) {
        // Every other plan at this level, not just the one we found: the local
        // list is a snapshot and may not be the whole set.
        setOwnPlans(prev => prev.map(p => (
          p.id === updated.id ? updated
            : p.board === plan.board && p.grade === plan.grade && p.isDefault
              ? { ...p, isDefault: false }
              : p
        )));
      } else {
        replace(updated);
      }
      setNote(isDefault
        ? `New ${level} students will be started on “${plan.name}”.`
        : `${level} has no default plan now.`);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  /** Which subject the schedule section is showing. Defaults to the plan's first,
   *  and follows the "Chapter dates" link on a subject row. */
  const [scheduleSubject, setScheduleSubject] = useState<string | null>(null);
  /**
   * ONE edit mode for the whole detail — the fields, the passes, and the chapter
   * grid below. Owned here rather than in either block, because two independent
   * edit states on one page would let someone edit dates while the fields above
   * them stayed read-only.
   */
  const { confirm, dialog } = useConfirm();
  const openPlan = (id: string) => { setSelectedId(id); setScheduleSubject(null); };

  /**
   * Adopt, with the busy and error states the raw callback had none of.
   *
   * The button called the host's handler and dropped whatever came back, so a
   * failed adopt looked exactly like a successful one: nothing moved, nothing
   * said why. A row action that writes must be able to report that it did not.
   */
  const runAdopt = async (p: StudyPlan) => {
    if (!onAdopt) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const outcome = await onAdopt(p);
      // Re-read the list. Adopting writes the reader's OWN plan — updating one
      // already listed, and creating one for any subject they had no plan for —
      // and the table was built once on mount, so neither appeared until the
      // panel was closed and reopened.
      await refresh();
      // Says what adoption actually did. It does NOT make their plan become the
      // source: it copies the source's dates INTO their own plan, which is what
      // keeps their ticks and lets them move a date without touching anyone
      // else's. Saying "X is now the plan you are following" contradicted the
      // Active badge, which correctly stayed on their own plan.
      /**
       * Says what landed, per subject.
       *
       * Taking a ten-subject plan writes ten documents and the writes are not
       * atomic, so "copied into your plan" was a claim the call could not make.
       * A subject skipped for the wrong syllabus is the thing a student most
       * needs told — silently taking nine of ten reads as success.
       */
      const took = outcome?.subjects?.length ?? 0;
      const missed = outcome?.notTaken ?? [];
      const verb = isMine(p) ? 'Now following' : 'Copied from';
      setNote([
        `${verb} “${p.name}”`,
        took ? ` — ${took} ${took === 1 ? 'subject' : 'subjects'}` : '',
        missed.length ? `. Not taken: ${missed.map(m => m.subject).join(', ')}.` : '.',
      ].join(''));
    } catch (e) {
      setError((e as Error).message || 'Could not take that plan');
    } finally { setBusy(false); }
  };
  /** Panel-wide permission AND this plan's own. */
  const mayEditThis = (p: StudyPlan) => canEdit && (canEditPlan?.(p) ?? true);
  /** The reader's OWN plan — taking it is following it, not copying it. Keyed on
   *  the panel's owner rather than on a name, since a copy carries the author's
   *  name in its own. */
  const isMine = (p: StudyPlan) => p.ownerKind === ownerKind && p.ownerId === ownerId;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const shownSubject = useMemo(() => {
    const covered = (selected?.subjects ?? []).map(x => x.subject);
    if (scheduleSubject && covered.includes(scheduleSubject)) return scheduleSubject;
    return covered[0] ?? null;
  }, [selected, scheduleSubject]);

  /**
   * Passes still missing a date on the plan being edited.
   *
   * A plan IS its dates: without them there is nothing for a student to be ahead
   * of or behind, the progress chart has no line to draw, and adopting it copies
   * an empty schedule. So Save is refused while any pass is undated — and names
   * them, because a disabled button that does not say why is a dead end.
   *
   * A plan whose passes are still the defaults carries none of its own yet, and
   * the editor shows those defaults — so they are counted the same way here.
   */
  const undatedPasses = useMemo(() => {
    if (!selected) return [];
    const passes = selected.passes?.length
      ? selected.passes
      : DEFAULT_STUDY_PASSES.map(p => ({ id: p.id, name: p.label, order: p.order }));
    return passes
      .filter(p => !(selected.passDates ?? {})[p.id])
      .map(p => p.name);
  }, [selected]);

  /**
   * A plan row's right-click menu.
   *
   * Only what has no column and no obvious place on the row. Setting the default
   * is exactly that: one plan in the list carries it, it is chosen by comparing
   * levels across rows, and a per-row button for it would spend a column on an
   * action almost every row must refuse.
   *
   * The conditions mirror `defaultable` in the editor — a published BuzzBrain
   * plan with a board and a grade — because the server enforces the same three,
   * and an item that is going to be rejected is worse than no item.
   */
  const rowMenu = (p: StudyPlan): RowMenuItem[] => {
    if (!canSetDefault) return [];
    const level = [p.board, p.grade && `Grade ${p.grade}`].filter(Boolean).join(' · ');
    if (p.isDefault) {
      return [{
        key: 'undefault',
        label: `Stop being the default for ${level}`,
        onSelect: () => void setAsDefault(p, false),
        disabled: busy,
      }];
    }
    if (p.ownerKind !== 'platform' || !p.board || !p.grade) return [];
    return [{
      key: 'default',
      // Named with the level, so a list where every plan shares a name still
      // says which one is about to become the default.
      label: `Make default for ${level}`,
      onSelect: () => void setAsDefault(p, true),
      disabled: busy || p.visibility !== 'listed',
    }];
  };

  const columns = useMemo<ResponsiveColumn<StudyPlan>[]>(() => {
    // Bound before filtering: chaining .filter() onto the literal drops the
    // annotation that types every render arg, which tsc only reports as a
    // cascade of implicit-any.
    const all: ResponsiveColumn<StudyPlan>[] = [
    {
      key: 'name',
      header: 'Plan',
      mobile: 'title',
      sortable: true,
      sortValue: p => p.name,
      render: p => (
        <span className="cs-plan-name-cell">
          <span className="cs-name-plain">
            {p.name}
            {/* The one row that answers "which am I actually following". */}
            {p.id === activePlanId && <span className="cs-active-badge">Active</span>}
            {/* A badge rather than a column: one row in a hundred carries it, and
                a column would spend width on ninety-nine blanks to say so. */}
            {p.isDefault && (
              <span className="cs-default-badge" title="New students at this board and grade start on this plan">
                Default
              </span>
            )}
          </span>
          {/* Only once non-zero: "0 students took this" on a plan published an
              hour ago reads as a verdict on it. */}
          {p.adoptions ? <span className="cs-meta">{p.adoptions} took it</span> : null}
          {/* What taking it today actually costs. On the row, so it is read while
              choosing rather than after. */}
          {planElapsed(p) && <span className="cs-plan-elapsed">{planElapsed(p)}</span>}
        </span>
      ),
    },
    {
      key: 'source',
      header: 'From',
      mobile: 'meta',
      sortable: true,
      sortValue: p => sourceLabel(p),
      render: p => <span className="cs-meta">{sourceLabel(p)}</span>,
    },
    {
      /**
       * WHO WROTE IT and WHO CAN TAKE IT are different facts, and the list used
       * to show only the first — so a class plan published to everyone still read
       * "Your class" and looked exactly as it had before. Its own column, beside
       * From, because on an oversight surface the second question is asked as
       * often as the first.
       */
      key: 'visibility',
      header: 'Visibility',
      mobile: 'meta',
      sortable: true,
      // Widest reach first, which is the order the question is asked in.
      sortValue: p => ({ listed: 3, class: 2, private: 1 })[p.visibility] ?? 0,
      render: p => <span className="cs-meta">{VISIBILITY_LABEL[p.visibility] ?? p.visibility}</span>,
    },
    {
      // The level, beside the subjects it determines. Two plans called "Steady"
      // are told apart by what they are FOR, and a plan with no level is the one
      // whose subjects each carry their own syllabus — worth seeing at a glance
      // rather than only after opening it.
      key: 'level',
      header: 'Board / Grade',
      mobile: 'meta',
      sortable: true,
      sortValue: p => `${p.board ?? ''} ${String(p.grade ?? '').padStart(2, '0')}`.trim(),
      render: p => (
        <span className="cs-meta">
          {p.board && p.grade ? `${p.board} · Grade ${p.grade}` : '—'}
        </span>
      ),
    },
    {
      key: 'subjects',
      header: 'Subjects',
      sortable: true,
      // By what they ARE, not how many: two plans covering one subject each sort
      // apart by subject, which is what someone scanning the column wants.
      sortValue: p => (p.subjects ?? []).map(x => x.subject).sort().join(', '),
      render: p => (
        <span className="cs-meta">
          {(p.subjects ?? []).length === 0
            ? '—'
            /* Subjects, and only subjects. Appending each one's syllabus was an
               attempt to tell two identically-named personal plans apart, and it
               made every other row — a seven-subject plan especially — a wall of
               repetition to fix one edge case. */
            : (p.subjects ?? []).map(x => subjectName?.(x.subject) ?? x.subject).join(', ')}
        </span>
      ),
    },
    {
      key: 'passes',
      header: 'Passes',
      sortable: true,
      sortValue: p => (p.passes ?? []).length,
      render: p => (
        (p.passes ?? []).length === 0 ? <span className="cs-meta">—</span> : (
          /* One pass per line WITH its date. Run together on one line the names
             were a list of words; the date beside each is what makes the column
             answer "when does this plan actually run". */
          <span className="cs-pass-lines">
            {[...p.passes].sort((a, b) => a.order - b.order).map(x => (
              <span key={x.id} className="cs-pass-line">
                <span className="cs-name-plain">{x.name}</span>
                <span className={`cs-meta${p.passDates?.[x.id] ? '' : ' cs-pass-undated'}`}>
                  {formatPlanDate(p.passDates?.[x.id]) ?? NO_DATE}
                </span>
              </span>
            ))}
          </span>
        )
      ),
    },
    {
      key: 'changed',
      header: 'Changed',
      mobile: 'meta',
      sortable: true,
      // Sorted on the raw ISO string, which orders correctly and does not depend
      // on how the cell chooses to format it.
      sortValue: p => p.updatedAt ?? '',
      render: p => (
        /* Who and when in one cell. Two columns spent a third of the table's
           width on one fact, and the passes — which are what a reader compares
           plans by — were squeezed into three wrapped lines because of it. */
        <span className="cs-meta cs-changed-cell">
          <span>{p.updatedByName ?? '—'}</span>
          {p.updatedAt && <span className="cs-changed-when">{p.updatedAt.slice(0, 10)}</span>}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      mobile: 'accessory',
      render: p => (
        <span className="cs-row-buttons">
          {/* Editing lives in the dialog now, where the plan being edited is on
              screen. On the row it was a second door to the same place, and the
              row already opens it. */}
          {/* Every plan the reader is not already following offers this. The
              active row offers nothing; it is already what it would become.

              Two different acts behind one column, so they carry two labels.
              Someone else's plan is COPIED — you get your own version and the
              original is untouched — and the label says so, because a student who
              thinks they are switching to a shared plan will expect the author's
              later edits to follow them. Your own plan is simply made active:
              nothing is copied, and there is no second version to keep track of. */}
          {onAdopt && p.id !== activePlanId && (
            <button
              type="button" className="cs-btn cs-btn--sm"
              disabled={busy}
              title={isMine(p)
                ? 'Follow this plan. Its dates become the ones on your grid.'
                : 'Copies these dates into a plan of your own. Yours to change afterwards.'}
              onClick={e => { e.stopPropagation(); void runAdopt(p); }}
            >{isMine(p) ? 'Adopt' : 'Copy and Adopt'}</button>
          )}
          {/* Publishing and taking it back are ONE control, because they are one
              act with different arguments — the same reason the server has one
              route for both. It used to be Share, going dead and reading
              "Shared" once used, so a published plan offered no way back and an
              oversight surface had to grow its own Unlist button beside it. */}
          {canPublish && canEdit && canPublishPlan(p) && (
            p.visibility === 'listed' ? (
              <button
                type="button" className="cs-link cs-icon--danger"
                disabled={busy}
                // Adopters keep their copy — saying so stops this reading as a
                // retraction.
                title="Stops anyone new finding it. Students who already took it keep their own copy."
                onClick={e => { e.stopPropagation(); void setVisibility(p, 'private'); }}
              >Unlist</button>
            ) : (
              <button
                type="button" className="cs-link"
                disabled={busy}
                title="Lets other students on this syllabus find and take a copy of this plan"
                onClick={e => { e.stopPropagation(); void setVisibility(p, 'listed'); }}
              >Share</button>
            )
          )}
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ];
    return omitColumns?.length ? all.filter(c => !omitColumns.includes(c.key)) : all;
  },
  [subjectName, onAdopt, busy, activePlanId,
    canPublish, canEdit, canEditPlan, canPublishPlan, ownerKind, ownerId, omitColumns]);

  if (loading) return <div className="cs"><p className="cs-muted">Loading…</p></div>;

  // ── The list ──────────────────────────────────────────────────────────
  if (!selected) {
    return (
      /* No frame. The list is a full-width table with its own header row and
         borders — a card around it drew a second, competing edge and stole width
         from the columns. Same reasoning as the editor, which already drops it. */
      <div className="cs cs--plain">
        {/* Filters and New plan share the row: both act on the LIST rather than
            on any row in it, and the button on a line of its own left a strip of
            empty page between the filters and the table they filter. */}
        <div className="cs-list-head">
          <div className="cs-list-head-filters">{listHeader}</div>
          {/* Before New plan, because both make a plan and this one makes the
              more specific kind — the general act reads better last. */}
          {headerActions}
          {canEdit && (
            <button type="button" className="cs-btn" disabled={busy} onClick={() => void create()}>
              {busy ? 'Opening…' : 'New plan'}
            </button>
          )}
        </div>


        {plans.length === 0 ? (
          <p className="cs-muted">
            No plans yet. A plan is a named schedule — &ldquo;Steady&rdquo;, &ldquo;Catch-up
            from October&rdquo; — covering one or more subjects, and you can write more
            than one.
          </p>
        ) : (
          <ResponsiveTable
            rows={plans}
            rowKey={p => p.id}
            columns={columns}
            className="cs-plan-list-table"
            rowClassName={() => 'cs-plan-table-row'}
            onRowClick={p => openPlan(p.id)}
            rowMenu={rowMenu}
          />
        )}

        {note && <p className="cs-note-ok">{note}</p>}
        {error && <p className="cs-error">{error}</p>}
        {dialog}
      </div>
    );
  }

  const saveBlockedBecause = !draft?.name.trim()
    ? 'Give the plan a name first.'
    : undatedPasses.length > 0
      ? `Set a date for ${undatedPasses.join(', ')} before saving — a plan is its dates.`
      : undefined;

  // ── The detail: two boxes, on the page or in a dialog ─────────────────
  const editable = mayEditThis(selected);
  const actions = (
    <span className="cs-detail-actions">
      {editable && (editing ? (
        <>
          <button
            type="button" className="cs-btn" disabled={busy || !!saveBlockedBecause}
            title={saveBlockedBecause}
            onClick={() => void saveDraft(selected)}
          >{busy ? 'Saving…' : 'Save'}</button>
          <button
            type="button" className="cs-link"
            onClick={() => void cancelEdit()}
          >Cancel</button>
        </>
      ) : (
        <>
          <button
            type="button" className="cs-btn"
            onClick={() => {
              setDraft({
                name: selected.name,
                description: selected.description ?? '',
                subjects: (selected.subjects ?? []).map(x => x.subject),
                board: selected.board,
                grade: selected.grade,
                isDefault: selected.isDefault,
              });
              setEditing(true);
            }}
          >Edit plan</button>
          <button
            type="button" className="cs-link cs-icon--danger" disabled={busy}
            title="Delete this plan. Students who already took it keep their own copy."
            onClick={() => void remove(selected)}
          >Delete plan</button>
        </>
      ))}
      {onAdopt && !editing && (
        <button
          type="button" className="cs-btn" disabled={busy}
          title="Copies these dates into your own plan. Yours to change afterwards."
          onClick={() => void onAdopt(selected)}
        >Adopt this plan</button>
      )}
    </span>
  );

  const body = (
    <>
      {error && <p className="cs-error">{error}</p>}

      {/* Box 1 — what the plan IS. */}
      <PlanEditor
        api={api}
        plan={selected}
        onPlanChange={replace}
        subjectName={subjectName}
        availableSubjects={availableSubjects}
        onSetVisibility={v => void setVisibility(selected, v)}
        canPublish={canPublish && canPublishPlan(selected)}
        boardGradeOptions={boardGradeOptions}
        canSetDefault={canSetDefault}
        canEdit={editable}
        editing={editing}
        draft={draft}
        onDraftChange={setDraft}
      />

      {/* Box 2 — how it gets there. A sibling, never nested. */}
      {shownSubject ? (
        <PlanChapterSchedule
          api={api}
          plan={selected}
          subject={shownSubject}
          onSubjectChange={setScheduleSubject}
          subjectName={subjectName}
          chapters={chaptersFor?.(shownSubject) ?? []}
          canEdit={editable}
          editing={editing}
          onPlanChange={replace}
        />
      ) : (
        <div className="cs">
          <div className="cs-head"><h3>Chapter schedule</h3></div>
          <p className="cs-muted">
            {editable
              ? 'Add a subject above, then its chapters can be dated here.'
              : 'This plan covers no subjects yet.'}
          </p>
        </div>
      )}
    </>
  );

  const close = () => {
    if (editing) { void cancelEdit(); return; }
    setSelectedId(null); setEditing(false); setDraft(null);
  };

  // A dialog keeps the list in place. Inline detail on a page that already has a
  // pass strip and a chapter grid below it pushed everything out of reach.
  if (detailIn === 'dialog') {
    return (
      <div className="cs-detail">
        {/*
          * Edit and Delete live here; Adopt and Share do not.
          *
          * The split is by whose plan it is. Editing and deleting act on the plan
          * you are LOOKING at and are the reason you opened it — and without them
          * a dialog opened in edit mode had no Save, which is how a plan could be
          * filled in and then lost. Adopting and sharing act on your schedule
          * rather than on the plan, so they stay on the row where you chose it.
          */}
        <Modal
          isOpen
          onClose={close}
          title={selected.name}
          size="xl"
        >
          {/* Top right of the dialog, above the record they act on — in the
              footer they sat below a scrolling chapter grid, so on a long plan
              the way to edit it was off screen. */}
          {editable && (
            <div className="cs-dialog-actions">
            {editing ? (
              <>
                <button type="button" className="cs-link" onClick={() => void cancelEdit()}>Cancel</button>
                <button
                  type="button" className="cs-btn" disabled={busy || !!saveBlockedBecause}
                  title={saveBlockedBecause}
                  onClick={() => void saveDraft(selected)}
                >{busy ? 'Saving…' : 'Save'}</button>
              </>
            ) : (
              <>
                <button
                  type="button" className="cs-link cs-icon--danger" disabled={busy}
                  title="Delete this plan. Students who already took it keep their own copy."
                  onClick={() => void remove(selected)}
                >Delete plan</button>
                <button
                  type="button" className="cs-btn"
                  onClick={() => {
                    setDraft({
                      name: selected.name,
                      description: selected.description ?? '',
                      subjects: (selected.subjects ?? []).map(x => x.subject),
                      // Carried, not omitted: `saveDraft` compares the draft's
                      // level against the plan's, and a draft that had never
                      // heard of the plan's level read as a re-levelling — the
                      // one edit that clears every chapter date.
                      board: selected.board,
                      grade: selected.grade,
                      isDefault: selected.isDefault,
                    });
                    setEditing(true);
                  }}
                >Edit plan</button>
              </>
            )}
            </div>
          )}
          {body}
        </Modal>
        {dialog}
      </div>
    );
  }

  return (
    <div className="cs-detail">
      <div className="cs-detail-back">
        <button type="button" className="cs-link" onClick={close}>‹ All plans</button>
        {actions}
      </div>

      <h2 className="cs-detail-title">{selected.name}</h2>
      {body}
      {dialog}
    </div>
  );
}
